import { useMemo } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  AlertCircle,
  BellRing,
  CheckCircle2,
  ChevronRight,
  Target,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { ApiError, getAlarms, getBudgets, getCosts, getToken } from "../api/client.js";
import type { AccountKey, AlarmsResponse, BudgetItem } from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { applyDemo } from "../lib/demo.js";
import { formatCurrency, formatPct, formatTime } from "../lib/format.js";
import type { PageId } from "../components/layout/Shell.js";
import { AreaSparkline, StackedBars } from "../components/charts.js";
import { Card, EmptyState, KpiCard, Pill, ProgressBar, SectionTitle, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";

// Local mirror of the server's /api/costs/daily contract; not yet in api/types.ts.
interface DailyCostSeries {
  account: string;
  label: string;
  amounts: number[];
}

interface DailyCostsResponse {
  days: string[];
  series: DailyCostSeries[];
  generatedAt: string;
}

async function getDailyCosts(): Promise<DailyCostsResponse> {
  const token = getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch("/api/costs/daily", { headers });
  } catch (err) {
    throw new ApiError(0, err instanceof Error ? err.message : "network error");
  }
  if (!res.ok) throw new ApiError(res.status, `daily costs unavailable (${res.status})`);
  const body = (await res.json()) as Partial<DailyCostsResponse>;
  const days = Array.isArray(body.days) ? body.days.filter((d) => typeof d === "string") : [];
  const series = Array.isArray(body.series)
    ? body.series.filter(
        (s): s is DailyCostSeries =>
          typeof s?.account === "string" && typeof s?.label === "string" && Array.isArray(s?.amounts),
      )
    : [];
  return applyDemo("/api/costs/daily", {
    days,
    series,
    generatedAt: typeof body.generatedAt === "string" ? body.generatedAt : new Date().toISOString(),
  });
}

const ACCOUNT_ORDER: AccountKey[] = ["prod", "staging", "management", "shared"];

const ACCOUNT_COLOR: Record<AccountKey, string> = {
  prod: "var(--acct-prod)",
  staging: "var(--acct-staging)",
  management: "var(--acct-dev)",
  shared: "var(--acct-shared)",
};

const TONE_SOFT: Record<Tone, { bg: string; fg: string }> = {
  alarm: { bg: "var(--tone-alarm-bg)", fg: "var(--tone-alarm-fg)" },
  ok: { bg: "var(--tone-ok-bg)", fg: "var(--tone-ok-fg)" },
  warn: { bg: "var(--tone-warn-bg)", fg: "var(--tone-warn-fg)" },
  nodata: { bg: "var(--tone-nodata-bg)", fg: "var(--tone-nodata-fg)" },
  info: { bg: "var(--tone-info-bg)", fg: "var(--tone-info-fg)" },
  accent: { bg: "var(--accent-bg)", fg: "var(--accent)" },
};

function accountKeyFor(text: string): AccountKey | null {
  const t = text.toLowerCase();
  if (t.includes("prod")) return "prod";
  if (t.includes("stag")) return "staging";
  if (t.includes("shared")) return "shared";
  if (t.includes("manage") || t.includes("payer") || t.includes("platform")) return "management";
  return null;
}

function moneyCompact(v: number): string {
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(Math.abs(v) >= 10000 ? 1 : 2)}k`;
  return `$${v.toFixed(0)}`;
}

function money0(v: number): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `$${v.toFixed(0)}`;
  }
}

function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function monthName(d: Date): string {
  return d.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
}

function deltaTone(pct: number): Tone {
  if (pct > 15) return "alarm";
  if (pct > 3) return "warn";
  if (pct < -1) return "ok";
  return "nodata";
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function budgetShortName(name: string): string {
  return name.replace(/^jammsante-/, "").replace(/-monthly$/, "");
}

function budgetIsFlagged(b: BudgetItem): boolean {
  if (b.status !== "under") return true;
  return b.limit > 0 && (b.actualSpend > b.limit || (b.forecastedSpend ?? 0) > b.limit);
}

function budgetForAccount(budgets: BudgetItem[], key: AccountKey): BudgetItem | null {
  const aliases: Record<AccountKey, string[]> = {
    prod: ["prod", "production"],
    staging: ["staging", "stage"],
    shared: ["shared"],
    management: ["management", "payer", "platform"],
  };
  const wanted = aliases[key];
  return (
    budgets.find((b) => {
      const tokens = b.name.toLowerCase().split(/[^a-z0-9]+/);
      return wanted.some((w) => tokens.includes(w));
    }) ?? null
  );
}

interface AttentionEntry {
  id: string;
  icon: ReactNode;
  tone: Tone;
  title: string;
  meta: string;
  page: PageId;
}

function buildAttention(
  alarms: AlarmsResponse | null,
  budgets: BudgetItem[] | null,
): AttentionEntry[] {
  const items: AttentionEntry[] = [];
  for (const a of alarms?.alarms ?? []) {
    if (a.state !== "ALARM") continue;
    const meta = ["ALARM", a.metric ?? a.accountLabel, a.updatedAt ? `since ${formatTime(a.updatedAt)}` : null]
      .filter(Boolean)
      .join(" · ");
    items.push({
      id: `alarm-${a.account}-${a.name}`,
      icon: <BellRing size={15} strokeWidth={1.75} />,
      tone: "alarm",
      title: a.name,
      meta,
      page: "alarms",
    });
  }
  for (const b of budgets ?? []) {
    if (!budgetIsFlagged(b)) continue;
    const usesForecast = (b.forecastedSpend ?? 0) > b.actualSpend;
    const spend = usesForecast ? (b.forecastedSpend ?? b.actualSpend) : b.actualSpend;
    const pct = b.limit > 0 ? ` · ${((spend / b.limit) * 100).toFixed(0)}%` : "";
    items.push({
      id: `budget-${b.name}`,
      icon: <Target size={15} strokeWidth={1.75} />,
      tone: "warn",
      title: b.name,
      meta: `${usesForecast ? "Forecast" : "Actual"} ${money0(spend)} vs ${money0(b.limit)} limit${pct}`,
      page: "budgets",
    });
  }
  for (const a of alarms?.alarms ?? []) {
    if (a.state !== "INSUFFICIENT_DATA") continue;
    items.push({
      id: `nodata-${a.account}-${a.name}`,
      icon: <AlertCircle size={15} strokeWidth={1.75} />,
      tone: "nodata",
      title: a.name,
      meta: ["Insufficient data", a.metric ?? a.accountLabel].join(" · "),
      page: "alarms",
    });
  }
  return items;
}

interface AccountCardData {
  key: AccountKey;
  label: string;
  id: string | null;
  color: string;
  pillText: string;
  pillTone: Tone;
  points: number[];
  mtd: number | null;
  budget: BudgetItem | null;
  forecast: number | null;
  alarmLine: string;
}

const attnRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 11,
  width: "100%",
  padding: "9px 8px",
  border: "none",
  borderRadius: 10,
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: "var(--font-body)",
};

const ellipsis: CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

interface OverviewPageProps {
  refreshKey: number;
  onNavigate: (page: PageId) => void;
}

export function OverviewPage({ refreshKey, onNavigate }: OverviewPageProps) {
  const costs = useApi(getCosts, [], refreshKey);
  const alarms = useApi(getAlarms, [], refreshKey);
  const budgets = useApi(getBudgets, [], refreshKey);
  const daily = useApi(getDailyCosts, [], refreshKey);

  const now = useMemo(
    () => (costs.data ? new Date(costs.data.generatedAt) : new Date()),
    [costs.data],
  );
  const elapsedDays = Math.max(1, now.getUTCDate() - 1);
  const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
  const daysInPrevMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).getUTCDate();
  const prevMonth = monthName(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));

  const samePeriodPrev = costs.data
    ? costs.data.grossPreviousMonth * Math.min(1, elapsedDays / daysInPrevMonth)
    : null;
  const mtdDelta =
    costs.data && samePeriodPrev && samePeriodPrev > 0
      ? (costs.data.grossMonthToDate / samePeriodPrev - 1) * 100
      : null;
  const fcDelta =
    costs.data?.forecastMonthEnd != null && costs.data.grossPreviousMonth > 0
      ? (costs.data.forecastMonthEnd / costs.data.grossPreviousMonth - 1) * 100
      : null;

  const alarmSummary = alarms.data?.summary ?? null;
  const flaggedBudgets = (budgets.data?.budgets ?? []).filter(budgetIsFlagged);
  const budgetTotal = budgets.data?.budgets.length ?? 0;
  const anyActualOver = flaggedBudgets.some((b) => b.limit > 0 && b.actualSpend > b.limit);

  const budgetFootnote = (() => {
    if (!budgets.data) return budgets.error ? `budgets unavailable · ${budgets.error}` : "loading budgets";
    if (flaggedBudgets.length === 0) return `of ${budgetTotal} budgets · all on track`;
    const names = flaggedBudgets.map((b) => budgetShortName(b.name));
    const detail =
      names.length <= 2
        ? `${names.join(" & ")} ${anyActualOver ? "over limit" : "forecast over limit"}`
        : `${names.length} ${anyActualOver ? "over limit" : "forecast over limit"}`;
    return `of ${budgetTotal} budgets · ${detail}`;
  })();

  const attention = useMemo(
    () => buildAttention(alarms.data, budgets.data?.budgets ?? null),
    [alarms.data, budgets.data],
  );

  const dailyChart = useMemo(() => {
    if (!daily.data || daily.data.days.length === 0 || daily.data.series.length === 0) return null;
    const { days, series } = daily.data;
    const ordered = [...series].sort((a, b) => {
      const ai = ACCOUNT_ORDER.indexOf(accountKeyFor(a.label) ?? ("" as AccountKey));
      const bi = ACCOUNT_ORDER.indexOf(accountKeyFor(b.label) ?? ("" as AccountKey));
      return (ai === -1 ? ACCOUNT_ORDER.length : ai) - (bi === -1 ? ACCOUNT_ORDER.length : bi);
    });
    const chartSeries = ordered.map((s) => {
      const key = accountKeyFor(s.label);
      return {
        label: s.label,
        color: key ? ACCOUNT_COLOR[key] : "var(--acct-dev)",
        values: days.map((_, i) => s.amounts[i] ?? 0),
      };
    });
    const totals = days.map((_, i) => chartSeries.reduce((sum, s) => sum + s.values[i], 0));
    const med = median(totals);
    const max = Math.max(...totals);
    const maxIdx = totals.indexOf(max);
    const anomalyIndex = med > 0 && max > 2 * med ? maxIdx : undefined;
    const mid = Math.floor((days.length - 1) / 2);
    const xLabels =
      days.length > 2 ? [dayLabel(days[0]), dayLabel(days[mid]), dayLabel(days[days.length - 1])] : days.map(dayLabel);
    return { chartSeries, xLabels, anomalyIndex, lastDay: dayLabel(days[days.length - 1]) };
  }, [daily.data]);

  const accountCards = useMemo<AccountCardData[]>(() => {
    const present = new Set<AccountKey>();
    for (const s of alarms.data?.byAccount ?? []) present.add(s.account);
    for (const c of costs.data?.byAccount ?? []) {
      const k = accountKeyFor(c.label);
      if (k) present.add(k);
    }
    return ACCOUNT_ORDER.filter((k) => present.has(k)).map((key) => {
      const summary = alarms.data?.byAccount.find((s) => s.account === key) ?? null;
      const cost = costs.data?.byAccount.find((c) => accountKeyFor(c.label) === key) ?? null;
      const spark =
        daily.data?.series.find((s) => (cost && s.account === cost.key) || accountKeyFor(s.label) === key) ?? null;
      const budget = budgetForAccount(budgets.data?.budgets ?? [], key);
      const mtd = cost?.amount ?? null;
      const forecast =
        budget?.forecastedSpend ?? (mtd !== null ? (mtd / elapsedDays) * daysInMonth : null);
      const pillTone: Tone = summary
        ? summary.alarm > 0
          ? "alarm"
          : summary.insufficient > 0
            ? "nodata"
            : "ok"
        : "nodata";
      const pillText = summary ? (summary.alarm > 0 ? "Alarm" : summary.insufficient > 0 ? "No data" : "OK") : "No data";
      const parts: string[] = [];
      if (summary) {
        if (summary.alarm > 0) parts.push(`${summary.alarm} alarm${summary.alarm > 1 ? "s" : ""}`);
        if (summary.insufficient > 0) parts.push(`${summary.insufficient} no data`);
        parts.push(`${summary.ok} OK`);
      } else {
        parts.push("no alarm data");
      }
      return {
        key,
        label: summary?.accountLabel ?? cost?.label ?? key,
        id: cost?.key ?? null,
        color: ACCOUNT_COLOR[key],
        pillText,
        pillTone,
        points: spark?.amounts ?? [],
        mtd,
        budget,
        forecast,
        alarmLine: parts.join(" · "),
      };
    });
  }, [alarms.data, costs.data, budgets.data, daily.data, elapsedDays, daysInMonth]);

  return (
    <>
      <style>{".ov-attn:hover{background:var(--bg-subtle)}.ov-attn:active{transform:scale(0.99)}"}</style>

      <div className="grid-kpi">
        <KpiCard
          label={`Month-to-date — ${monthName(now)}`}
          icon={<Wallet size={15} strokeWidth={1.75} />}
          value={costs.data ? formatCurrency(costs.data.grossMonthToDate) : "—"}
          badge={mtdDelta !== null ? formatPct(mtdDelta) : undefined}
          badgeTone={mtdDelta !== null ? deltaTone(mtdDelta) : undefined}
          footnote={
            costs.data && samePeriodPrev !== null
              ? `vs ${formatCurrency(samePeriodPrev)} same period in ${prevMonth}`
              : costs.error
                ? `costs unavailable · ${costs.error}`
                : "loading costs"
          }
          onClick={() => onNavigate("costs")}
        />
        <KpiCard
          label="Forecast — month end"
          icon={<TrendingUp size={15} strokeWidth={1.75} />}
          value={costs.data?.forecastMonthEnd != null ? moneyCompact(costs.data.forecastMonthEnd) : "—"}
          badge={fcDelta !== null ? `${formatPct(fcDelta)} vs ${prevMonth}` : undefined}
          badgeTone={fcDelta !== null ? deltaTone(fcDelta) : undefined}
          footnote={
            costs.data
              ? `${prevMonth} actual ${money0(costs.data.grossPreviousMonth)} · run-rate basis`
              : "loading forecast"
          }
          onClick={() => onNavigate("costs")}
        />
        <KpiCard
          label="Alarms"
          icon={<BellRing size={15} strokeWidth={1.75} />}
          value={alarmSummary ? String(alarmSummary.alarm) : "—"}
          valueColor={alarmSummary && alarmSummary.alarm > 0 ? "var(--tone-alarm-fg)" : undefined}
          badge={alarmSummary ? (alarmSummary.alarm > 0 ? "in alarm" : "all OK") : undefined}
          badgeTone={alarmSummary && alarmSummary.alarm > 0 ? "alarm" : "ok"}
          footnote={
            alarmSummary
              ? `${alarmSummary.ok} OK · ${alarmSummary.insufficient} insufficient data · ${alarmSummary.total} total`
              : alarms.error
                ? `alarms unavailable · ${alarms.error}`
                : "loading alarms"
          }
          onClick={() => onNavigate("alarms")}
        />
        <KpiCard
          label="Budgets"
          icon={<Target size={15} strokeWidth={1.75} />}
          value={budgets.data ? `${flaggedBudgets.length} flagged` : "—"}
          badge={flaggedBudgets.length > 0 ? (anyActualOver ? "over limit" : "forecast") : undefined}
          badgeTone="warn"
          footnote={budgetFootnote}
          onClick={() => onNavigate("budgets")}
        />
      </div>

      <div className="grid-split">
        <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <SectionTitle
            title="Daily spend — last 30 days"
            subtitle={`Unblended cost, all accounts${dailyChart ? ` · through ${dailyChart.lastDay}` : ""}`}
            actions={dailyChart?.chartSeries.map((s) => (
              <span
                key={s.label}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 11.5,
                  color: "var(--fg-subtle)",
                }}
              >
                <StatusDot square color={s.color} />
                {s.label}
              </span>
            ))}
          />
          {dailyChart ? (
            <StackedBars
              series={dailyChart.chartSeries}
              labels={dailyChart.xLabels}
              anomalyIndex={dailyChart.anomalyIndex}
              height={150}
            />
          ) : daily.loading ? (
            <div
              style={{
                height: 170,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "var(--fg-faint)",
              }}
            >
              Loading daily spend...
            </div>
          ) : (
            <EmptyState
              tone="nodata"
              icon={<AlertCircle size={19} strokeWidth={1.75} />}
              title="Daily spend unavailable"
              message={daily.error ?? "No daily cost data for the last 30 days."}
            />
          )}
        </Card>

        <Card style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <SectionTitle
            title="Needs attention"
            actions={
              attention.length > 0 ? (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "var(--fg-faint)",
                  }}
                >
                  {attention.length} items
                </span>
              ) : undefined
            }
          />
          {attention.length === 0 ? (
            alarms.loading || budgets.loading ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "var(--fg-faint)",
                }}
              >
                Checking alarms and budgets...
              </div>
            ) : (
              <EmptyState
                icon={<CheckCircle2 size={19} strokeWidth={1.75} />}
                title="All quiet"
                message="No alarms, no budget flags. A calm morning for a change."
              />
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, margin: "0 -8px" }}>
              {attention.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="ov-attn"
                  style={attnRowStyle}
                  onClick={() => onNavigate(item.page)}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      flex: "none",
                      borderRadius: 8,
                      background: TONE_SOFT[item.tone].bg,
                      color: TONE_SOFT[item.tone].fg,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--fg)", ...ellipsis }}>
                      {item.title}
                    </span>
                    <span style={{ fontSize: 11.5, color: "var(--fg-subtle)", ...ellipsis }}>{item.meta}</span>
                  </span>
                  <ChevronRight size={14} strokeWidth={1.75} style={{ flex: "none", color: "var(--fg-faint)" }} />
                </button>
              ))}
            </div>
          )}
        </Card>
      </div>

      {accountCards.length > 0 && (
        <div className="grid-accounts">
          {accountCards.map((a) => {
            const pct = a.budget && a.budget.limit > 0 && a.forecast !== null ? (a.forecast / a.budget.limit) * 100 : null;
            const barColor =
              pct === null
                ? "var(--tone-nodata-dot)"
                : pct > 100
                  ? "var(--tone-alarm-dot)"
                  : pct > 90
                    ? "var(--tone-warn-dot)"
                    : a.color;
            const pctColor =
              pct === null
                ? "var(--fg-faint)"
                : pct > 100
                  ? "var(--tone-alarm-fg)"
                  : pct > 90
                    ? "var(--tone-warn-fg)"
                    : "var(--fg-subtle)";
            return (
              <Card
                key={a.key}
                onClick={() => onNavigate("costs")}
                style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StatusDot square size={9} color={a.color} />
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--fg)", flex: 1, ...ellipsis }}>
                    {a.label}
                  </span>
                  <Pill tone={a.pillTone}>{a.pillText}</Pill>
                </span>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-faint)" }}>
                  {a.id ?? "—"}
                </span>
                {a.points.length > 0 ? (
                  <AreaSparkline points={a.points} color={a.color} height={34} />
                ) : (
                  <span
                    style={{
                      display: "block",
                      height: 34,
                      borderRadius: 6,
                      background: "var(--bg-subtle)",
                    }}
                  />
                )}
                <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>MTD</span>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: "var(--fg)" }}>
                    {a.mtd !== null ? formatCurrency(a.mtd) : "—"}
                  </span>
                </span>
                <span style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>Forecast vs budget</span>
                    <span className="mono" style={{ fontSize: 11.5, color: pctColor }}>
                      {pct !== null && a.budget ? `${pct.toFixed(0)}% of ${moneyCompact(a.budget.limit)}` : "no budget"}
                    </span>
                  </span>
                  <ProgressBar pct={pct ?? 0} color={barColor} />
                </span>
                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--fg-subtle)",
                    borderTop: "1px solid var(--divider)",
                    paddingTop: 9,
                  }}
                >
                  {a.alarmLine}
                </span>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}
